import { alpha, Stack, Typography, useTheme } from "@mui/material";
import { HubCard } from "~/components/hub-card";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { WarningAmberOutlined as WarningIcon } from "@mui/icons-material";
import { Metrics } from "~/lib/arrow";
import { useMetrics } from "~/hooks";
import { FleetIcon } from "~/icons";

const getExposureLink = (scope: SpaceOrWorkspaceScope | undefined) =>
  scope ? `/space/dashboard/critical-exposures?${scope.params}` : undefined;

interface CriticalExposuresProps {
  scope?: SpaceOrWorkspaceScope;
}

export const CriticalExposuresCard = ({ scope }: CriticalExposuresProps) => {
  const theme = useTheme();
  const { criticalExposures, isLoading } = useMetrics({
    entityMrn: scope?.mrn || "",
    metricMrns: [Metrics.CriticalExposures],
  });

  const hasNoExposures = !isLoading && criticalExposures?.exposures_count === 0;
  const hasExposures = !isLoading && criticalExposures?.exposures_count > 0;

  // apply neutral color when exposure count is still loading
  const exposuresCardStyle = {
    "& .MuiPaper-root": {
      border: `2px solid ${hasExposures ? theme.palette.critical?.main : hasNoExposures ? theme.palette.none.main : "0"}`,
      backgroundColor: `${hasExposures ? alpha(theme.palette.critical?.light, 0.09) : hasNoExposures ? alpha(theme.palette.none?.light, 0.09) : ""}`,
    },
  };

  return (
    <HubCard
      loading={isLoading}
      title="CRITICAL EXPOSURES"
      tagline="To avert a likely breach, fix these findings today."
      icon={<WarningIcon />}
      data-testid="critical-exposures-card"
      to={getExposureLink(scope)}
      sx={exposuresCardStyle}
    >
      <Stack direction="row" gap={3}>
        <Typography
          fontSize={32}
          fontWeight={700}
          data-testid="critical-exposures-count"
        >
          {criticalExposures?.exposures_count}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <FleetIcon />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: "40px" }}
            data-testid="critical-exposures-assets-count"
          >
            {criticalExposures?.assets_count} Assets
          </Typography>
        </Stack>
      </Stack>
    </HubCard>
  );
};

CriticalExposuresCard.displayName = "CriticalExposuresCard";
